import type { AppShellIconKey } from "../../../../../../shared/frontend/layout/appShellIcons";
import type { AuthInfo } from "../api/types";
import { hasAnyPermissionIn } from "../auth/permissionMatch";
import { tenantCapabilityCatalogDefault, tenantCapabilityWithFallback } from "../auth/tenantCapabilitiesRead";
import { Permissions } from "../constants/permissions";
import { rowAccessPermissions, type PermissionRowKey } from "../permissions/permissionRows";

/**
 * Ein Katalog je Menübereich — einzige Quelle für Menüeinträge, Übersichtskarten und Router-Guards
 * (plans/2026-10-31-rechtesystem-neuordnung.md, Abschnitt 6). Rechte kommen aus der Rechtezeile
 * (`row`), nie aus einer eigenen Liste. Einstellungsseiten führt `pages/settings/settingsCatalog`,
 * Werkzeuge `pages/tools/toolsCatalog`; beide verweisen ebenfalls auf Rechtezeilen.
 */
export type AreaEntry = {
  path: string;
  titleKey: string;
  descriptionKey?: string;
  menuIcon: AppShellIconKey;
  cardIcon: string;
  row: PermissionRowKey;
  /** Weitere Schlüssel, die die Seite ebenfalls öffnen (z. B. nur prüfen im Postausgang). */
  extraPermissions?: string[];
  /** Alle genannten Capabilities müssen aktiv sein. */
  capabilities?: string[];
  /** Mandanteneinstellung des Personalmoduls, ohne die der Eintrag fehlt. */
  staffFeature?: "hr" | "timeTracking";
};

export type AreaAccessContext = {
  hasAnyPermission: (...permissions: string[]) => boolean;
  capabilityEnabled: (key: string) => boolean;
  staffHrEnabled?: boolean;
  staffTimeTrackingEnabled?: boolean;
};

export const financeArea: AreaEntry[] = [
  { path: "/finance/receivables", titleKey: "Receivables", descriptionKey: "Receivables card description", menuIcon: "FunnelPlotOutlined", cardIcon: "FunnelPlotOutlined", row: "receivables", capabilities: ["finance"] },
  { path: "/finance/bank-statement", titleKey: "Bank statement", descriptionKey: "Fetch statement and show transaction list", menuIcon: "BankOutlined", cardIcon: "ReconciliationOutlined", row: "bankStatement", capabilities: ["finance"] },
  { path: "/finance/open-items", titleKey: "Open items", descriptionKey: "Manage open and overdue dues", menuIcon: "FileTextOutlined", cardIcon: "DollarOutlined", row: "openItems", capabilities: ["finance"] },
  { path: "/finance/accounting-exports", titleKey: "Accounting exports", descriptionKey: "Accounting exports card description", menuIcon: "ExportOutlined", cardIcon: "ExportOutlined", row: "accountingExports", capabilities: ["finance"] },
  { path: "/finance/debt-collection-cases", titleKey: "Debt collection cases", descriptionKey: "Debt collection cases card description", menuIcon: "AlertOutlined", cardIcon: "FileProtectOutlined", row: "debtCollectionCases", capabilities: ["finance", "debtCollection"] },
  { path: "/finance/sepa-direct-debit-runs", titleKey: "Direct debits", descriptionKey: "Create, export and post direct debit runs", menuIcon: "SwapOutlined", cardIcon: "CreditCardOutlined", row: "sepa", capabilities: ["finance"] },
  { path: "/finance/reminders", titleKey: "Reminders", descriptionKey: "Create/send reminders", menuIcon: "BellOutlined", cardIcon: "MailOutlined", row: "reminders", capabilities: ["finance", "reminders"] },
  { path: "/finance/sepa-blocked", titleKey: "SEPA blocks", descriptionKey: "SEPA blocks card description", menuIcon: "StopOutlined", cardIcon: "PauseCircleOutlined", row: "payments", capabilities: ["finance"] }
];

export const salesArea: AreaEntry[] = [
  { path: "/leads", titleKey: "Leads", descriptionKey: "Manage and edit leads", menuIcon: "BranchesOutlined", cardIcon: "ShopOutlined", row: "leads", capabilities: ["leadManager"] },
  { path: "/sales/signing", titleKey: "Close contract", descriptionKey: "Close a new contract for a person", menuIcon: "FileTextOutlined", cardIcon: "FileTextOutlined", row: "contractClosing" },
  { path: "/sales/articles", titleKey: "Sell articles", descriptionKey: "Create an article sale for a person", menuIcon: "ShoppingCartOutlined", cardIcon: "ShoppingOutlined", row: "articleSales", capabilities: ["articleSales"] }
];

export const communicationArea: AreaEntry[] = [
  { path: "/communication/inbox", titleKey: "Inbox", descriptionKey: "Inbox card description", menuIcon: "InboxOutlined", cardIcon: "InboxOutlined", row: "communication.inbox", capabilities: ["communication"] },
  { path: "/communication/send", titleKey: "Send", descriptionKey: "Send messages", menuIcon: "SendOutlined", cardIcon: "MailOutlined", row: "communication.send", capabilities: ["communication"] },
  { path: "/communication/outbound", titleKey: "Dispatch", menuIcon: "ExportOutlined", cardIcon: "SendOutlined", row: "communication.outbound", extraPermissions: [Permissions.CommunicationOutboundReview], capabilities: ["communication"] }
];

export const staffArea: AreaEntry[] = [
  { path: "/staff/schedule", titleKey: "Staff schedule", menuIcon: "ScheduleOutlined", cardIcon: "ScheduleOutlined", row: "staff.schedule", capabilities: ["staffScheduling"] },
  { path: "/staff/absences", titleKey: "Absences", menuIcon: "CalendarOutlined", cardIcon: "CalendarOutlined", row: "staff.absences", capabilities: ["staffScheduling"] },
  { path: "/staff/availabilities", titleKey: "Availabilities", menuIcon: "CalendarOutlined", cardIcon: "CalendarOutlined", row: "staff.absences", capabilities: ["staffScheduling"] },
  { path: "/staff/vacation", titleKey: "Vacation management", menuIcon: "CalendarOutlined", cardIcon: "CalendarOutlined", row: "staff.vacation", capabilities: ["staffScheduling"], staffFeature: "hr" },
  { path: "/staff/worktime", titleKey: "Working time", menuIcon: "ClockCircleOutlined", cardIcon: "ClockCircleOutlined", row: "staff.time", extraPermissions: [Permissions.StaffTimeApprove], capabilities: ["staffScheduling"], staffFeature: "timeTracking" },
  { path: "/staff/payroll", titleKey: "Payroll preparation", menuIcon: "FileTextOutlined", cardIcon: "FileTextOutlined", row: "staff.payroll", capabilities: ["staffScheduling"], staffFeature: "hr" },
  { path: "/staff/hour-account", titleKey: "Hour balance", menuIcon: "HistoryOutlined", cardIcon: "HistoryOutlined", row: "staff.payroll", capabilities: ["staffScheduling"], staffFeature: "hr" }
];

export function areaEntryPermissions(entry: AreaEntry): string[] {
  return [...rowAccessPermissions(entry.row), ...(entry.extraPermissions ?? [])];
}

/** Recht vorhanden — ohne Capability- und Mandantenschalterprüfung (die übernehmen Guard bzw. Sperrkarte). */
export function hasAreaEntryPermission(entry: AreaEntry, ctx: Pick<AreaAccessContext, "hasAnyPermission">): boolean {
  return ctx.hasAnyPermission(...areaEntryPermissions(entry));
}

/** Menü: Eintrag nur, wenn Recht, Module und Mandantenschalter passen. */
export function isAreaEntryVisible(entry: AreaEntry, ctx: AreaAccessContext): boolean {
  if (!hasAreaEntryPermission(entry, ctx)) return false;
  if ((entry.capabilities ?? []).some((key) => !ctx.capabilityEnabled(key))) return false;
  if (entry.staffFeature === "hr" && ctx.staffHrEnabled === false) return false;
  if (entry.staffFeature === "timeTracking" && ctx.staffTimeTrackingEnabled !== true) return false;
  return true;
}

/**
 * Zugriffskontext aus der Sitzung. Capability-Vorgaben ausschließlich aus dem Backend-Katalog
 * (`tenantCapabilityCatalogDefault`), keine eigenen Rückfallwerte im Menü (Audit F26).
 */
export function areaAccessContextFromAuth(auth: AuthInfo | null): AreaAccessContext {
  const moduleFallback: Record<string, boolean | undefined> = {
    calendar: auth?.moduleCalendarEnabled,
    leadManager: auth?.moduleLeadManagerEnabled
  };
  return {
    hasAnyPermission: (...permissions) => hasAnyPermissionIn(auth?.permissions, permissions, auth?.role),
    capabilityEnabled: (key) => tenantCapabilityWithFallback(auth?.capabilities, key, moduleFallback[key] ?? tenantCapabilityCatalogDefault(key)),
    staffHrEnabled: auth?.staffHrEnabled !== false,
    staffTimeTrackingEnabled: auth?.staffTimeTrackingEnabled === true
  };
}

export type AreaCard = { titleKey: string; descriptionKey?: string; iconKey: string; path: string; capabilityKey?: string };

/**
 * Übersichtskarten eines Bereichs. Gefiltert wird nach Recht und Mandantenschalter; ein nicht
 * freigeschaltetes Modul zeigt die Karte gesperrt (`capabilityKey`, Sperrdarstellung in `NavCardGrid`).
 */
export function areaCards(entries: AreaEntry[], ctx: AreaAccessContext): AreaCard[] {
  return entries
    .filter((entry) => hasAreaEntryPermission(entry, ctx))
    .filter((entry) => !(entry.staffFeature === "hr" && ctx.staffHrEnabled === false))
    .filter((entry) => !(entry.staffFeature === "timeTracking" && ctx.staffTimeTrackingEnabled !== true))
    .map((entry) => ({
      titleKey: entry.titleKey,
      descriptionKey: entry.descriptionKey,
      iconKey: entry.cardIcon,
      path: entry.path,
      capabilityKey: entry.capabilities?.find((key) => !ctx.capabilityEnabled(key)) ?? entry.capabilities?.[entry.capabilities.length - 1]
    }));
}

const allAreaEntries = [...financeArea, ...salesArea, ...communicationArea, ...staffArea];

export function areaEntryForPath(path: string): AreaEntry {
  const entry = allAreaEntries.find((e) => e.path === path);
  if (!entry) throw new Error(`Kein Bereichskatalog-Eintrag für ${path}`);
  return entry;
}
